import { useState } from 'react';
import { Bot, Plus } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Select } from '@/shared/ui/Select';
import { Spinner, EmptyState } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Dialog } from '@/shared/ui/Dialog';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { formatDateTime } from '@/lib/utils';
import { useReports, useGenerateReport } from './reports.api';
import { ReportDetailDialog } from './ReportDetailDialog';
import { useProjects } from '@/features/projects/projects.api';
import type { ReportStatus } from '@/shared/api/types';

const STATUS_FILTER: { value: ReportStatus | ''; label: string }[] = [
  { value: '', label: 'Alle Status' },
  { value: 'GENERATED', label: 'KI-Entwurf' },
  { value: 'REVIEW_REQUIRED', label: 'Prüfung nötig' },
  { value: 'READY_TO_SEND', label: 'Bereit' },
  { value: 'AWAITING_CUSTOMER', label: 'Wartet auf Kunde' },
  { value: 'CUSTOMER_CONFIRMED', label: 'Bestätigt' },
  { value: 'CUSTOMER_REJECTED', label: 'Abgelehnt' },
];

export function ReportsPage() {
  const [status, setStatus] = useState<ReportStatus | ''>('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [genOpen, setGenOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const { data, isLoading } = useReports({ status });
  const { data: projects } = useProjects({});
  const generate = useGenerateReport();
  const push = useToast((s) => s.push);

  const onGenerate = async () => {
    if (!projectId) {
      push('Bitte einen Auftrag auswählen.', 'error');
      return;
    }
    try {
      const res = await generate.mutateAsync(projectId);
      push(res.message);
      setGenOpen(false);
      setProjectId('');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Erzeugen fehlgeschlagen.', 'error');
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Berichte</h1>
        <Button onClick={() => setGenOpen(true)}>
          <Plus className="h-4 w-4" /> KI-Entwurf erzeugen
        </Button>
      </div>

      <Select
        value={status}
        onChange={(e) => setStatus(e.target.value as ReportStatus | '')}
        className="mb-4 sm:w-56"
      >
        {STATUS_FILTER.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </Select>

      {isLoading ? (
        <Spinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="Keine Berichte"
          hint="Erzeugen Sie einen KI-Entwurf aus den Feldmeldungen eines Auftrags."
        />
      ) : (
        <ul className="space-y-2">
          {data.items.map((r) => (
            <li
              key={r.id}
              onClick={() => setDetailId(r.id)}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-medium text-slate-900">
                  {r.project.title} <span className="text-xs text-slate-400">v{r.version}</span>
                </p>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                <span>{formatDateTime(r.createdAt)}</span>
                {r._count && <span>{r._count.items} Positionen</span>}
                {r.approvedBy && <span>freigegeben: {r.approvedBy.displayName}</span>}
              </div>
              {r.summary && <p className="mt-1 truncate text-sm text-slate-600">{r.summary}</p>}
            </li>
          ))}
        </ul>
      )}

      {data && <p className="mt-3 text-xs text-slate-400">{data.total} Berichte</p>}

      <Dialog open={genOpen} onClose={() => setGenOpen(false)} title="KI-Entwurf erzeugen">
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <Bot className="mt-0.5 h-4 w-4 shrink-0" />
            Die KI strukturiert die Feldmeldungen zu einem Berichtsentwurf. Der Entwurf muss danach
            geprüft werden – er geht nie ungeprüft an den Kunden.
          </div>
          <Select label="Auftrag" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Bitte wählen…</option>
            {projects?.items
              .filter((p) => (p._count?.fieldReports ?? 0) > 0)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} ({p._count?.fieldReports} Meldungen)
                </option>
              ))}
          </Select>
          <Button className="w-full" onClick={onGenerate} loading={generate.isPending}>
            Entwurf erzeugen
          </Button>
        </div>
      </Dialog>

      <ReportDetailDialog reportId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
